import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  TablePagination,
  InputAdornment,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { adminGroupsAPI, debugAPI, employeeAPI, rulesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import RulesCatalog from './RulesCatalog';
import GroupRulesPanel, { GroupRuleValues, defaultGroupRuleValues } from './GroupRulesPanel';
import { EmployeeSearchFilter } from '../types/Employee';

interface AdminGroup {
  id: number;
  name: string;
  description: string;
  admin_manages_timesheets?: boolean;
  admin_manages_leaves?: boolean;
  timesheet_export_policy?: 'inherit' | 'allow' | 'deny';
  created_by: number;
  created_at: string;
  updated_at: string;
  member_count: number;
  employee_count: number;
  created_by_name: string;
  created_by_email: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  nome: string;
  cognome: string;
  role: string;
}

interface Employee {
  id: number;
  user_id?: number | null;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  matricola?: string;
  dipartimento?: string;
  ente?: string;
  stato?: string;
  data_cessazione?: string | null;
}

interface GroupEmployeesSummary {
  reference_date: string;
  total_assigned: number;
  visible_employees: number;
  scheduled_cessations: number;
  effective_cessations: number;
  deleted_employees: number;
}

interface GroupEmployeeNotice {
  employee_id: number;
  nome: string;
  cognome: string;
  codice_fiscale: string;
  matricola?: string | null;
  stato?: string;
  data_cessazione?: string | null;
  reason: 'scheduled_cessation' | 'effective_cessation' | 'deleted';
}

const toDateKey = (value?: string | null) => (value ? value.split('T')[0] : '');

const formatDateLabel = (value?: string | null) => {
  const dateKey = toDateKey(value);
  if (!dateKey) return '-';
  const [year, month, day] = dateKey.split('-');
  if (!year || !month || !day) return dateKey;
  return `${day}/${month}/${year}`;
};

const getEmployeeNoticeLabel = (reason: GroupEmployeeNotice['reason']) => {
  switch (reason) {
    case 'scheduled_cessation':
      return 'Cessazione programmata';
    case 'effective_cessation':
      return 'Cessato';
    case 'deleted':
      return 'Eliminato';
    default:
      return 'Situazione anagrafica';
  }
};

const getEmployeeStatusChipLabel = (employee: Employee, referenceDate?: string | null) => {
  if (!employee.data_cessazione) return null;
  const cessationDate = toDateKey(employee.data_cessazione);
  const comparisonDate = toDateKey(referenceDate) || toDateKey(new Date().toISOString());
  if (cessationDate && comparisonDate && cessationDate < comparisonDate) {
    return `Cessato ${formatDateLabel(employee.data_cessazione)}`;
  }
  return `Cessazione programmata ${formatDateLabel(employee.data_cessazione)}`;
};

const AdminGroupsManagement: React.FC = () => {
  const { user } = useAuth();
  const { isSuperAdmin, isAdmin, isGroupAdmin, loadingGroupCheck } = usePermissions();
  const [groups, setGroups] = useState<AdminGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog stati
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDialogMode, setGroupDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedGroup, setSelectedGroup] = useState<AdminGroup | null>(null);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [adminManagesTimesheets, setAdminManagesTimesheets] = useState(false);
  const [adminManagesLeaves, setAdminManagesLeaves] = useState(false);
  const [timesheetExportPolicy, setTimesheetExportPolicy] = useState<'inherit' | 'allow' | 'deny'>('inherit');
  const [groupRuleValues, setGroupRuleValues] = useState<GroupRuleValues>(defaultGroupRuleValues);
  const [pageTab, setPageTab] = useState(0);
  const [groupRulesPreview, setGroupRulesPreview] = useState<{ effects: string[]; visibility: Record<string, boolean> } | null>(null);

  // Tab per dettagli gruppo
  const [groupDetailsOpen, setGroupDetailsOpen] = useState(false);
  const [groupDetailsTab, setGroupDetailsTab] = useState(0);
  const [groupMembers, setGroupMembers] = useState<User[]>([]);
  const [groupEmployees, setGroupEmployees] = useState<Employee[]>([]);
  const [memberSearch, setMemberSearch] = useState('');

  const memberSearchTerm = memberSearch.trim().toLowerCase();
  const filteredGroupEmployees = memberSearchTerm
    ? groupEmployees.filter((employee) =>
        `${employee.cognome} ${employee.nome}`.toLowerCase().includes(memberSearchTerm)
        || `${employee.nome} ${employee.cognome}`.toLowerCase().includes(memberSearchTerm))
    : groupEmployees;
  const [groupEmployeesSummary, setGroupEmployeesSummary] = useState<GroupEmployeesSummary | null>(null);
  const [scheduledCessationEmployees, setScheduledCessationEmployees] = useState<GroupEmployeeNotice[]>([]);
  const [effectiveCessationEmployees, setEffectiveCessationEmployees] = useState<GroupEmployeeNotice[]>([]);
  const [excludedGroupEmployees, setExcludedGroupEmployees] = useState<GroupEmployeeNotice[]>([]);
  const [countDetailsOpen, setCountDetailsOpen] = useState(false);
  const [allAdmins, setAllAdmins] = useState<User[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  
  // Stati per invio credenziali
  const [sendingCreds, setSendingCreds] = useState(false);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [confirmSendDialog, setConfirmSendDialog] = useState(false);
  const [pendingRecipients, setPendingRecipients] = useState<Array<{
    id: number;
    name: string;
    email: string;
    type: 'admin' | 'employee';
  }>>([]);
  const [pendingUserIds, setPendingUserIds] = useState<number[]>([]);
  const [pendingExcludedCount, setPendingExcludedCount] = useState(0);
  const [generatedCredentials, setGeneratedCredentials] = useState<Array<{
    user_id: number;
    username: string;
    email: string;
    name: string;
    password: string;
  }>>([]);
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [addAdminMode, setAddAdminMode] = useState<'existing' | 'new' | 'fromEmployee'>('existing');
  
  // Stati per creare nuovo admin
  const [newAdminData, setNewAdminData] = useState({
    username: '',
    email: '',
    password: '',
    codice_fiscale: '',
    nome: '',
    cognome: '',
    role: 'admin' as 'admin' | 'manager' | 'employee',
    is_super_admin: false,
  });
  
  // Stati per convertire dipendente in admin
  const [selectedEmployeeForAdmin, setSelectedEmployeeForAdmin] = useState<number | null>(null);
  const [employeeAdminData, setEmployeeAdminData] = useState({
    username: '',
    password: '',
    is_super_admin: false,
  });
  
  // Filtri per ricerca dipendenti nel tab "Da Dipendente"
  const [employeeSearchFilter, setEmployeeSearchFilter] = useState<EmployeeSearchFilter>({
    nome: '',
    cognome: '',
    matricola: '',
    codice_fiscale: '',
    dipartimento: '',
    stato: '',
    ente: '',
    regione_sede: '',
    tipo_sede: '',
    ruolo: '',
    page: 1,
    limit: 20,
  });
  const [filteredEmployeesForAdmin, setFilteredEmployeesForAdmin] = useState<Employee[]>([]);
  const [filteredEmployeesForAdminTotal, setFilteredEmployeesForAdminTotal] = useState(0);
  const [loadingEmployeesForAdmin, setLoadingEmployeesForAdmin] = useState(false);
  const debounceTimerForAdminRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountForAdmin = useRef(true);
  
  // Stati per filtri e selezione dipendenti
  const [employeeFilter, setEmployeeFilter] = useState<EmployeeSearchFilter>({
    nome: '',
    cognome: '',
    matricola: '',
    codice_fiscale: '',
    dipartimento: '',
    stato: '',
    ente: '',
    regione_sede: '',
    tipo_sede: '',
    ruolo: '',
    page: 1,
    limit: 20,
  });
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [filteredEmployeesTotal, setFilteredEmployeesTotal] = useState(0);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [enti, setEnti] = useState<string[]>([]);
  const [regioni, setRegioni] = useState<string[]>([]);
  const [tipiSede, setTipiSede] = useState<string[]>([]);
  const [ruoli, setRuoli] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);


  useEffect(() => {
    if (isAdmin) {
      loadGroups();
    }
  }, [user?.id, isAdmin]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      // Determina se è super admin o admin normale (per ruolo o per gruppo)
      const isAdminUser = isSuperAdmin || user?.role === 'admin' || isGroupAdmin === true;
      const shouldLoadAllGroups = isSuperAdmin; // Solo super admin vede tutti i gruppi
      
      const response = shouldLoadAllGroups
        ? await adminGroupsAPI.getAllGroups()
        : await adminGroupsAPI.getMyGroups();
      setGroups(response.groups || []);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento dei gruppi');
    } finally {
      setLoading(false);
    }
  };

  const rulesToApiMap = (values: GroupRuleValues): Record<string, string> => ({
    'presence.admin_managed': values['presence.admin_managed'] ? 'true' : 'false',
    'presence.double_punch': values['presence.double_punch'] ? 'true' : 'false',
    'timesheet.hours_only': values['timesheet.hours_only'] ? 'true' : 'false',
    'leave.admin_managed': values['leave.admin_managed'] ? 'true' : 'false',
    'export.timesheet_policy': values['export.timesheet_policy'],
    'communication.send_credentials_email': values['communication.send_credentials_email'] ? 'true' : 'false',
  });

  const syncLegacyFromRules = (values: GroupRuleValues) => {
    setAdminManagesTimesheets(values['presence.admin_managed']);
    setAdminManagesLeaves(values['leave.admin_managed']);
    setTimesheetExportPolicy(values['export.timesheet_policy']);
  };

  const handleCreateGroup = () => {
    setGroupDialogMode('create');
    setGroupName('');
    setGroupDescription('');
    setGroupRuleValues(defaultGroupRuleValues);
    syncLegacyFromRules(defaultGroupRuleValues);
    setSelectedGroup(null);
    setGroupDialogOpen(true);
  };

  const handleEditGroup = async (group: AdminGroup) => {
    setGroupDialogMode('edit');
    setGroupName(group.name);
    setGroupDescription(group.description || '');
    setSelectedGroup(group);
    const initial: GroupRuleValues = {
      'presence.admin_managed': group.admin_manages_timesheets || false,
      'presence.double_punch': false,
      'timesheet.hours_only': false,
      'leave.admin_managed': group.admin_manages_leaves || false,
      'export.timesheet_policy': group.timesheet_export_policy || 'inherit',
      'communication.send_credentials_email': true,
    };
    setGroupRuleValues(initial);
    syncLegacyFromRules(initial);
    try {
      const res = await rulesAPI.getGroupRules(group.id);
      const loaded = { ...initial };
      (res.rules || []).forEach((r: { key: string; value: string }) => {
        if (r.key === 'presence.admin_managed') loaded['presence.admin_managed'] = r.value === 'true';
        if (r.key === 'presence.double_punch') loaded['presence.double_punch'] = r.value === 'true';
        if (r.key === 'timesheet.hours_only') loaded['timesheet.hours_only'] = r.value === 'true';
        if (r.key === 'leave.admin_managed') loaded['leave.admin_managed'] = r.value === 'true';
        if (r.key === 'export.timesheet_policy') loaded['export.timesheet_policy'] = r.value as 'inherit' | 'allow' | 'deny';
        if (r.key === 'communication.send_credentials_email') loaded['communication.send_credentials_email'] = r.value === 'true';
      });
      setGroupRuleValues(loaded);
      syncLegacyFromRules(loaded);
    } catch {
      // usa valori legacy dal gruppo
    }
    setGroupDialogOpen(true);
  };

  const handleSaveGroup = async () => {
    try {
      syncLegacyFromRules(groupRuleValues);
      if (groupDialogMode === 'create') {
        const created = await adminGroupsAPI.createGroup({
          name: groupName,
          description: groupDescription,
          admin_manages_timesheets: groupRuleValues['presence.admin_managed'],
          admin_manages_leaves: groupRuleValues['leave.admin_managed'],
          timesheet_export_policy: groupRuleValues['export.timesheet_policy'],
        });
        const newGroupId = created.id;
        if (newGroupId) {
          await rulesAPI.setGroupRules(newGroupId, rulesToApiMap(groupRuleValues));
        }
        setSuccess('Gruppo creato con successo');
      } else {
        await adminGroupsAPI.updateGroup(selectedGroup!.id, {
          name: groupName,
          description: groupDescription,
          admin_manages_timesheets: groupRuleValues['presence.admin_managed'],
          admin_manages_leaves: groupRuleValues['leave.admin_managed'],
          timesheet_export_policy: groupRuleValues['export.timesheet_policy'],
        });
        await rulesAPI.setGroupRules(selectedGroup!.id, rulesToApiMap(groupRuleValues));
        setSuccess('Gruppo aggiornato con successo');
      }
      setGroupDialogOpen(false);
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel salvataggio del gruppo');
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo gruppo?')) {
      return;
    }
    try {
      await adminGroupsAPI.deleteGroup(groupId);
      setSuccess('Gruppo eliminato con successo');
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'eliminazione del gruppo');
    }
  };

  const handleViewGroupDetails = async (group: AdminGroup) => {
    setSelectedGroup(group);
    setMemberSearch('');
    setGroupDetailsOpen(true);
    setGroupDetailsTab(isSuperAdmin ? 0 : 0);
    setGroupRulesPreview(null);
    await loadGroupDetails(group.id);
    try {
      const preview = isSuperAdmin
        ? await rulesAPI.getGroupRulesPreview(group.id)
        : await rulesAPI.getMyGroupRulesReadOnly(group.id);
      setGroupRulesPreview({
        effects: preview.effects || [],
        visibility: preview.visibility_preview || preview.visibility || {},
      });
    } catch {
      setGroupRulesPreview(null);
    }
  };

  const loadGroupDetails = async (groupId: number) => {
    try {
      const promises: Promise<any>[] = [];
      
      // Solo super admin può vedere i membri (altri admin)
      if (isSuperAdmin) {
        promises.push(adminGroupsAPI.getGroupMembers(groupId));
      }
      
      // Carica dipendenti usando l'API appropriata
      if (isSuperAdmin) {
        promises.push(adminGroupsAPI.getGroupEmployees(groupId));
      } else {
        promises.push(adminGroupsAPI.getMyGroupEmployees(groupId));
      }
      
      const results = await Promise.all(promises);
      const employeeResult = isSuperAdmin ? results[1] : results[0];
      
      if (isSuperAdmin) {
        setGroupMembers(results[0].members || []);
      } else {
        setGroupMembers([]); // Admin normali non vedono altri admin
      }

      setGroupEmployees(employeeResult.employees || []);
      setGroupEmployeesSummary(employeeResult.summary || null);
      setScheduledCessationEmployees(employeeResult.scheduled_cessation_employees || []);
      setEffectiveCessationEmployees(employeeResult.effective_cessation_employees || []);
      setExcludedGroupEmployees(employeeResult.excluded_employees || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nel caricamento dei dettagli');
    }
  };

  const handleAddMember = async () => {
    if (!selectedGroup) return;
    
    try {
      let adminUserId: number;
      
      if (addAdminMode === 'existing') {
        // Aggiungi admin esistente
        if (!selectedAdminId) return;
        adminUserId = selectedAdminId;
        await adminGroupsAPI.addAdminToGroup(selectedGroup.id, adminUserId);
        setSuccess('Admin aggiunto al gruppo con successo');
      } else if (addAdminMode === 'new') {
        // Crea nuovo admin ex novo
        if (!newAdminData.username || !newAdminData.password || !newAdminData.email) {
          setError('Compila tutti i campi obbligatori');
          return;
        }
        const response = await debugAPI.createUser({
          ...newAdminData,
          role: 'admin',
          // Valori di default per campi obbligatori del database ma non necessari per un admin
          ore_lavoro: 0,
          giorni_lavoro: 0,
          data_apertura_posizione: new Date().toISOString().split('T')[0],
        });
        adminUserId = response.user.id;
        await adminGroupsAPI.addAdminToGroup(selectedGroup.id, adminUserId);
        setSuccess('Admin creato e aggiunto al gruppo con successo');
        // Reset form
        setNewAdminData({
          username: '',
          email: '',
          password: '',
          codice_fiscale: '',
          nome: '',
          cognome: '',
          role: 'admin',
          is_super_admin: false,
        });
      } else if (addAdminMode === 'fromEmployee') {
        // Aggiungi dipendente come admin (con o senza utente esistente)
        if (!selectedEmployeeForAdmin) {
          setError('Seleziona un dipendente');
          return;
        }
        
        // Trova il dipendente selezionato per verificare se ha già un utente
        const selectedEmployee = filteredEmployeesForAdmin.find(emp => emp.id === selectedEmployeeForAdmin);
        
        if (selectedEmployee?.user_id) {
          // Il dipendente ha già un utente, usa quello esistente
          adminUserId = selectedEmployee.user_id;
          await adminGroupsAPI.addAdminToGroup(selectedGroup.id, adminUserId);
          setSuccess('Dipendente (con utente esistente) aggiunto come admin al gruppo con successo');
        } else {
          // Il dipendente non ha utente, crea nuovo utente admin
          if (!employeeAdminData.username || !employeeAdminData.password) {
            setError('Username e password sono obbligatori per creare un nuovo utente');
            return;
          }
          const response = await employeeAPI.createUserForEmployee(selectedEmployeeForAdmin, {
            username: employeeAdminData.username,
            password: employeeAdminData.password,
            role: 'admin',
            is_super_admin: employeeAdminData.is_super_admin,
          });
          adminUserId = response.user.id;
          await adminGroupsAPI.addAdminToGroup(selectedGroup.id, adminUserId);
          setSuccess('Dipendente convertito in admin e aggiunto al gruppo con successo');
        }
        
        // Reset form
        setSelectedEmployeeForAdmin(null);
        setEmployeeAdminData({
          username: '',
          password: '',
          is_super_admin: false,
        });
      } else {
        return;
      }
      
      setAddMemberDialogOpen(false);
      setSelectedAdminId(null);
      setAddAdminMode('existing');
      await loadGroupDetails(selectedGroup.id);
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'aggiunta dell\'admin');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!selectedGroup) return;
    try {
      await adminGroupsAPI.removeAdminFromGroup(selectedGroup.id, userId);
      setSuccess('Admin rimosso dal gruppo con successo');
      await loadGroupDetails(selectedGroup.id);
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nella rimozione dell\'admin');
    }
  };

  const handleSendCredentialsToGroup = () => {
    if (!selectedGroup) return;
    
    if (groupMembers.length === 0) {
      setError('Il gruppo non ha membri a cui inviare le credenziali');
      return;
    }

    const recipients = groupMembers.map(member => ({
      id: member.id,
      name: `${member.cognome} ${member.nome}`,
      email: member.email,
      type: 'admin' as const,
    }));

    setPendingRecipients(recipients);
    setPendingUserIds(groupMembers.map(member => member.id));
    setPendingExcludedCount(0);
    setConfirmSendDialog(true);
  };

  const handleCloseCredentialsDialog = () => {
    setCredentialsDialog(false);
    setGeneratedCredentials([]);
  };

  const handleSendCredentialsToGroupEmployees = () => {
    if (!selectedGroup) return;
    
    if (groupEmployees.length === 0) {
      setError('Il gruppo non ha dipendenti a cui inviare le credenziali');
      return;
    }

    const employeesWithUser = groupEmployees.filter(emp => emp.user_id != null);
    const employeesWithoutUser = groupEmployees.filter(emp => emp.user_id == null);
    
    if (employeesWithUser.length === 0) {
      setError('Nessun dipendente del gruppo ha un utente associato. Crea prima gli utenti per i dipendenti.');
      return;
    }

    const recipients = employeesWithUser.map(emp => ({
      id: emp.user_id!,
      name: `${emp.cognome} ${emp.nome}`,
      email: '',
      type: 'employee' as const,
    }));

    setPendingRecipients(recipients);
    setPendingUserIds(employeesWithUser.map(emp => emp.user_id!));
    setPendingExcludedCount(employeesWithoutUser.length);
    setConfirmSendDialog(true);
  };

  const handleConfirmSendCredentials = async () => {
    if (!selectedGroup || pendingUserIds.length === 0) return;

    const userIdsToSend = [...pendingUserIds];
    const excludedCount = pendingExcludedCount;
    const isAdminRecipients = pendingRecipients.some(r => r.type === 'admin');

    try {
      setSendingCreds(true);
      setError('');
      setConfirmSendDialog(false);

      const response = await debugAPI.sendTempCredentials(userIdsToSend);

      setGeneratedCredentials(response.credentials || []);
      setCredentialsDialog(true);
      setPendingUserIds([]);
      setPendingExcludedCount(0);

      await loadGroupDetails(selectedGroup.id);

      if (response.email_failures > 0) {
        setError(`Credenziali generate per ${response.credentials?.length || 0} utenti, ma ${response.email_failures} email NON inviate: comunica le credenziali mostrate manualmente.`);
      } else if (excludedCount > 0) {
        setSuccess(`Credenziali inviate a ${response.credentials?.length || 0} dipendente/i (${excludedCount} senza utente esclusi)`);
      } else {
        setSuccess(
          isAdminRecipients
            ? `Credenziali inviate a ${response.credentials?.length || 0} membro/i del gruppo con successo!`
            : `Credenziali inviate a ${response.credentials?.length || 0} dipendente/i del gruppo con successo!`
        );
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore durante l\'invio delle credenziali');
    } finally {
      setSendingCreds(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!selectedGroup) return;
    
    // Se ci sono dipendenti selezionati, aggiungi quelli
    if (selectedEmployeeIds.length > 0) {
      try {
        // Aggiungi tutti i dipendenti selezionati usando l'API appropriata
        const addEmployeeAPI = isSuperAdmin 
          ? (groupId: number, employeeId: number) => adminGroupsAPI.addEmployeeToGroup(groupId, employeeId)
          : (groupId: number, employeeId: number) => adminGroupsAPI.addEmployeeToMyGroup(groupId, employeeId);
        
        await Promise.all(
          selectedEmployeeIds.map(id => addEmployeeAPI(selectedGroup.id, id))
        );
        setSuccess(`${selectedEmployeeIds.length} dipendente/i aggiunto/i al gruppo con successo`);
        setAddEmployeeDialogOpen(false);
        setSelectedEmployeeIds([]);
        setEmployeeFilter({
          nome: '',
          cognome: '',
          matricola: '',
          codice_fiscale: '',
          dipartimento: '',
          stato: '',
          ente: '',
          regione_sede: '',
          tipo_sede: '',
          ruolo: '',
          page: 1,
          limit: 20,
        });
        await loadGroupDetails(selectedGroup.id);
        loadGroups();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Errore nell\'aggiunta dei dipendenti');
      }
      return;
    }
    
    // Fallback per compatibilità con il vecchio metodo (singolo dipendente)
    if (!selectedEmployeeId) return;
    try {
      if (isSuperAdmin) {
        await adminGroupsAPI.addEmployeeToGroup(selectedGroup.id, selectedEmployeeId);
      } else {
        await adminGroupsAPI.addEmployeeToMyGroup(selectedGroup.id, selectedEmployeeId);
      }
      setSuccess('Dipendente aggiunto al gruppo con successo');
      setAddEmployeeDialogOpen(false);
      setSelectedEmployeeId(null);
      await loadGroupDetails(selectedGroup.id);
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'aggiunta del dipendente');
    }
  };

  const handleRemoveEmployee = async (employeeId: number) => {
    if (!selectedGroup) return;
    try {
      if (isSuperAdmin) {
        await adminGroupsAPI.removeEmployeeFromGroup(selectedGroup.id, employeeId);
      } else {
        await adminGroupsAPI.removeEmployeeFromMyGroup(selectedGroup.id, employeeId);
      }
      setSuccess('Dipendente rimosso dal gruppo con successo');
      await loadGroupDetails(selectedGroup.id);
      loadGroups();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nella rimozione del dipendente');
    }
  };

  const loadAllAdmins = async () => {
    try {
      const response = await debugAPI.getUsers();
      const admins = (response.users || []).filter((u: User) => u.role === 'admin');
      setAllAdmins(admins);
    } catch (err) {
      // Errore caricamento admin
    }
  };

  const loadAllEmployees = async () => {
    try {
      const response = await employeeAPI.searchEmployees({});
      setAllEmployees(response.employees || []);
    } catch (err) {
      // Errore caricamento dipendenti
    }
  };

  // Carica dipendenti filtrati per il tab "Da Dipendente"
  const loadFilteredEmployeesForAdmin = async () => {
    try {
      setLoadingEmployeesForAdmin(true);
      
      // Assicurati che ci sia almeno un limite valido
      const filterToUse = {
        ...employeeSearchFilter,
        limit: employeeSearchFilter.limit || 100,
        page: employeeSearchFilter.page || 1,
      };
      
      const data = await employeeAPI.searchEmployees(filterToUse);
      const employees = data.employees || [];
      
      // Mostra TUTTI i dipendenti, anche quelli con user_id
      // L'utente può comunque selezionare solo quelli senza user_id (verificato nel form)
      
      setFilteredEmployeesForAdmin(employees);
      // Usa il totale originale per la paginazione
      setFilteredEmployeesForAdminTotal(data.total || employees.length);
    } catch (err) {
      // Errore caricamento dipendenti filtrati
      setError('Errore nel caricamento dei dipendenti: ' + (err instanceof Error ? err.message : 'Errore sconosciuto'));
      setFilteredEmployeesForAdmin([]);
      setFilteredEmployeesForAdminTotal(0);
    } finally {
      setLoadingEmployeesForAdmin(false);
    }
  };

  // Debounce per i campi di ricerca testuali nel tab "Da Dipendente"
  useEffect(() => {
    if (addAdminMode !== 'fromEmployee') return;
    if (isInitialMountForAdmin.current) {
      return;
    }
    if (debounceTimerForAdminRef.current) {
      clearTimeout(debounceTimerForAdminRef.current);
    }
    debounceTimerForAdminRef.current = setTimeout(() => {
      loadFilteredEmployeesForAdmin();
    }, 1500);
    return () => {
      if (debounceTimerForAdminRef.current) {
        clearTimeout(debounceTimerForAdminRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSearchFilter.nome, employeeSearchFilter.cognome, employeeSearchFilter.codice_fiscale, employeeSearchFilter.matricola]);

  // Caricamento immediato per dropdown e paginazione nel tab "Da Dipendente"
  useEffect(() => {
    if (addAdminMode !== 'fromEmployee') return;
    if (isInitialMountForAdmin.current) {
      return;
    }
    loadFilteredEmployeesForAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSearchFilter.dipartimento, employeeSearchFilter.stato, employeeSearchFilter.ente, employeeSearchFilter.regione_sede, employeeSearchFilter.tipo_sede, employeeSearchFilter.ruolo, employeeSearchFilter.page, employeeSearchFilter.limit]);

  const handleEmployeeSearchFilterChange = (field: keyof EmployeeSearchFilter, value: string | number) => {
    setEmployeeSearchFilter(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : parseInt(value.toString()) || 1),
    }));
  };

  const handlePageChangeForAdmin = (event: unknown, newPage: number) => {
    setEmployeeSearchFilter(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPageForAdmin = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setEmployeeSearchFilter(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  // Carica le liste per i filtri
  const loadFilterData = async () => {
    try {
      const [deptsRes, entiRes, regioniRes, tipiSedeRes, ruoliRes] = await Promise.all([
        employeeAPI.getDepartments(),
        employeeAPI.getEnti(),
        employeeAPI.getRegioni(),
        employeeAPI.getTipiSede(),
        employeeAPI.getRuoli(),
      ]);
      setDepartments(deptsRes.departments || []);
      // getEnti() restituisce già un array direttamente
      setEnti(Array.isArray(entiRes) ? entiRes : []);
      setRegioni(regioniRes.regioni || []);
      setTipiSede(tipiSedeRes.tipi_sede || []);
      setRuoli(ruoliRes.ruoli || []);
    } catch (err) {}
  };

  // Carica dipendenti filtrati
  const loadFilteredEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const data = await employeeAPI.searchEmployees(employeeFilter);
      const employees = data.employees || [];
      // Filtra i dipendenti già nel gruppo
      const groupEmployeeIds = groupEmployees.map(e => e.id);
      const availableEmployees = employees.filter(
        (emp: Employee) => !groupEmployeeIds.includes(emp.id)
      );
      setFilteredEmployees(availableEmployees);
      setFilteredEmployeesTotal(data.total || 0);
    } catch (err) {
      // Errore caricamento dipendenti filtrati
      setError('Errore nel caricamento dei dipendenti');
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Debounce per i campi di ricerca testuali
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      loadFilteredEmployees();
    }, 1500);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeFilter.nome, employeeFilter.cognome, employeeFilter.codice_fiscale, employeeFilter.matricola]);

  // Caricamento immediato per dropdown e paginazione
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    loadFilteredEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeFilter.dipartimento, employeeFilter.stato, employeeFilter.ente, employeeFilter.regione_sede, employeeFilter.tipo_sede, employeeFilter.ruolo, employeeFilter.page, employeeFilter.limit]);

  const handleEmployeeFilterChange = (field: keyof EmployeeSearchFilter, value: string | number) => {
    setEmployeeFilter(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : parseInt(value.toString()) || 1),
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setEmployeeFilter(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setEmployeeFilter(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSelectEmployee = (employeeId: number) => {
    setSelectedEmployeeIds(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAllEmployees = () => {
    const pageEmployeeIds = filteredEmployees.map(e => e.id);
    const allSelected = pageEmployeeIds.length > 0 && pageEmployeeIds.every(id => selectedEmployeeIds.includes(id));
    setSelectedEmployeeIds(prev =>
      allSelected
        ? prev.filter(id => !pageEmployeeIds.includes(id))
        : [...new Set([...prev, ...pageEmployeeIds])]
    );
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Caricamento informazioni utente...
        </Alert>
      </Container>
    );
  }

  if (loadingGroupCheck) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Accesso negato: privilegi admin richiesti. Ruolo attuale: {user?.role || 'non definito'}
        </Alert>
      </Container>
    );
  }

  if (loading && groups.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Caricamento gruppi...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {isSuperAdmin ? 'Struttura gruppi e deleghe' : 'Gruppi assegnati'}
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateGroup}
          >
            Nuovo Gruppo
          </Button>
        )}
      </Box>

      {isSuperAdmin && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={pageTab} onChange={(_, v) => setPageTab(v)}>
            <Tab label="Gruppi" />
            <Tab label="Catalogo Regole" />
          </Tabs>
        </Box>
      )}

      {pageTab === 1 && isSuperAdmin ? (
        <RulesCatalog />
      ) : (
        <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {!loading && groups.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isSuperAdmin 
            ? 'Nessun gruppo creato. Crea il primo gruppo per iniziare.'
            : 'Non appartieni a nessun gruppo. Contatta un super admin per essere assegnato a un gruppo.'}
        </Alert>
      )}

      {groups.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Dipendenti</TableCell>
                <TableCell>Creato da</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>
                    {group.name}
                    {group.admin_manages_timesheets && (
                      <Chip
                        label="Admin gestisce cartellini"
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                        title="I dipendenti di questo gruppo non ricevono email di attivazione e non possono accedere al cartellino mensile"
                      />
                    )}
                    {group.admin_manages_leaves && (
                      <Chip
                        label="Admin gestisce ferie/permessi"
                        size="small"
                        color="secondary"
                        sx={{ ml: 1 }}
                        title="I dipendenti di questo gruppo non possono inserire autonomamente richieste di ferie e permessi"
                      />
                    )}
                    {group.timesheet_export_policy && group.timesheet_export_policy !== 'inherit' && (
                      <Chip
                        label={group.timesheet_export_policy === 'allow' ? 'Export cartellino: consentito' : 'Export cartellino: negato'}
                        size="small"
                        color={group.timesheet_export_policy === 'allow' ? 'success' : 'warning'}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{group.description || '-'}</TableCell>
                  <TableCell>
                    {group.member_count === 0 ? (
                      <Chip 
                        label="Nessun admin" 
                        size="small" 
                        color="warning"
                        title="Questo gruppo non ha admin assegnati. I dipendenti sono visibili solo ai super admin."
                      />
                    ) : (
                      <Chip label={group.member_count} size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={group.employee_count} size="small" color="primary" />
                  </TableCell>
                  <TableCell>{group.created_by_name}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleViewGroupDetails(group)}
                      title="Dettagli"
                    >
                      <PeopleIcon />
                    </IconButton>
                    {isSuperAdmin && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleEditGroup(group)}
                          title="Modifica"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteGroup(group.id)}
                          title="Elimina"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
        </>
      )}

      {/* Dialog creazione/modifica gruppo */}
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {groupDialogMode === 'create' ? 'Nuovo Gruppo' : 'Modifica Gruppo'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome Gruppo"
            fullWidth
            variant="outlined"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrizione"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <GroupRulesPanel
            groupId={groupDialogMode === 'edit' ? selectedGroup?.id : undefined}
            values={groupRuleValues}
            onChange={(v) => {
              setGroupRuleValues(v);
              syncLegacyFromRules(v);
            }}
            showPreview
          />
          {groupDialogMode === 'create' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                Puoi creare il gruppo senza admin. Potrai aggiungere admin e dipendenti successivamente.
                I dipendenti di gruppi senza admin sono visibili solo ai super admin.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleSaveGroup} variant="contained" disabled={!groupName.trim()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog dettagli gruppo */}
      <Dialog
        open={groupDetailsOpen}
        onClose={() => {
          setGroupDetailsOpen(false);
          setCountDetailsOpen(false);
        }}
        disableEnforceFocus={confirmSendDialog || credentialsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Dettagli Gruppo: {selectedGroup?.name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {groupRulesPreview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Regole attive del gruppo (area riservata dipendente)
              </Typography>
              {groupRulesPreview.effects.map((effect, idx) => (
                <Typography key={idx} variant="body2">• {effect}</Typography>
              ))}
            </Alert>
          )}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={isSuperAdmin ? groupDetailsTab : 0} 
              onChange={(_, v) => setGroupDetailsTab(v)}
            >
              {isSuperAdmin && <Tab label={`Admin (${groupMembers.length})`} />}
              <Tab label={`Dipendenti (${groupEmployees.length})`} />
            </Tabs>
          </Box>

          {isSuperAdmin && groupDetailsTab === 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Admin Membri</Typography>
                <Box display="flex" gap={1}>
                  {groupMembers.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleSendCredentialsToGroup}
                      disabled={sendingCreds}
                    >
                      {sendingCreds ? <CircularProgress size={16} /> : 'Invia Credenziali'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={async () => {
                      await loadAllAdmins();
                      await loadAllEmployees();
                      setAddMemberDialogOpen(true);
                    }}
                  >
                    Aggiungi Admin
                  </Button>
                </Box>
              </Box>
              {groupMembers.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Questo gruppo non ha admin assegnati
                  </Typography>
                  <Typography variant="body2">
                    I dipendenti assegnati a questo gruppo saranno visibili solo ai super admin.
                    Aggiungi almeno un admin al gruppo per permettere agli admin normali di vedere i dipendenti.
                  </Typography>
                </Alert>
              )}
              <List>
                {groupMembers.map((member) => (
                  <ListItem key={member.id}>
                    <ListItemText
                      primary={`${member.cognome} ${member.nome}`}
                      secondary={member.email}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => {
                          if (groupMembers.length === 1) {
                            if (window.confirm(
                              'Rimuovendo l\'ultimo admin, i dipendenti di questo gruppo saranno visibili solo ai super admin. Continuare?'
                            )) {
                              handleRemoveMember(member.id);
                            }
                          } else {
                            handleRemoveMember(member.id);
                          }
                        }}
                        color="error"
                      >
                        <PersonRemoveIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {groupMembers.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Nessun admin assegnato a questo gruppo
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {((isSuperAdmin && groupDetailsTab === 1) || !isSuperAdmin) && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Dipendenti Assegnati</Typography>
                <Box display="flex" gap={1}>
                  {groupEmployees.length > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleSendCredentialsToGroupEmployees}
                      disabled={sendingCreds}
                    >
                      {sendingCreds ? <CircularProgress size={16} /> : 'Invia Credenziali'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={async () => {
                      await loadFilterData();
                      isInitialMount.current = true;
                      setEmployeeFilter({
                        nome: '',
                        cognome: '',
                        matricola: '',
                        codice_fiscale: '',
                        dipartimento: '',
                        stato: '',
                        ente: '',
                        regione_sede: '',
                        tipo_sede: '',
                        ruolo: '',
                        page: 1,
                        limit: 20,
                      });
                      setSelectedEmployeeIds([]);
                      setAddEmployeeDialogOpen(true);
                      // Carica i dipendenti dopo un breve delay per permettere al dialog di aprirsi
                      setTimeout(() => {
                        isInitialMount.current = false;
                        loadFilteredEmployees();
                      }, 100);
                    }}
                  >
                    Aggiungi Dipendente
                  </Button>
                </Box>
              </Box>
              {groupEmployeesSummary && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Situazione al {formatDateLabel(groupEmployeesSummary.reference_date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Il gruppo mantiene operativi anche i dipendenti cessati e segnala separatamente cessazioni programmate, cessazioni effettive ed eventuali eliminazioni.
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => setCountDetailsOpen(true)}>
                      Perche questo conteggio?
                    </Button>
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                    <Chip label={`Assegnati: ${groupEmployeesSummary.total_assigned}`} variant="outlined" />
                    <Chip label={`Visibili oggi: ${groupEmployeesSummary.visible_employees}`} color="primary" variant="outlined" />
                    {groupEmployeesSummary.scheduled_cessations > 0 && (
                      <Chip label={`Cessazioni programmate: ${groupEmployeesSummary.scheduled_cessations}`} color="warning" variant="outlined" />
                    )}
                    {groupEmployeesSummary.effective_cessations > 0 && (
                      <Chip label={`Cessati effettivi: ${groupEmployeesSummary.effective_cessations}`} variant="outlined" />
                    )}
                    {groupEmployeesSummary.deleted_employees > 0 && (
                      <Chip label={`Eliminati: ${groupEmployeesSummary.deleted_employees}`} color="error" variant="outlined" />
                    )}
                  </Box>
                </Paper>
              )}
              {groupEmployeesSummary && (groupEmployeesSummary.scheduled_cessations > 0 || groupEmployeesSummary.effective_cessations > 0 || groupEmployeesSummary.deleted_employees > 0) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Alla data del {formatDateLabel(groupEmployeesSummary.reference_date)} il conteggio operativo include anche i rapporti gia cessati; gli eliminati restano separati.
                </Alert>
              )}
              <TextField
                label="Cerca per nome o cognome"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <List>
                {filteredGroupEmployees.map((employee) => (
                  <ListItem key={employee.id}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body1">{`${employee.cognome} ${employee.nome}`}</Typography>
                          {getEmployeeStatusChipLabel(employee, groupEmployeesSummary?.reference_date) && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={getEmployeeStatusChipLabel(employee, groupEmployeesSummary?.reference_date)}
                            />
                          )}
                        </Box>
                      }
                      secondary={`${employee.codice_fiscale} - ${employee.dipartimento || 'N/A'}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveEmployee(employee.id)}
                        color="error"
                      >
                        <PersonRemoveIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {filteredGroupEmployees.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {groupEmployees.length === 0
                      ? 'Nessun dipendente assegnato a questo gruppo'
                      : 'Nessun dipendente corrisponde alla ricerca'}
                  </Typography>
                )}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDetailsOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={countDetailsOpen}
        onClose={() => setCountDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Spiegazione Conteggio Dipendenti</DialogTitle>
        <DialogContent dividers>
          {groupEmployeesSummary && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Riferimento conteggio: {formatDateLabel(groupEmployeesSummary.reference_date)}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                <Chip label={`Assegnati: ${groupEmployeesSummary.total_assigned}`} variant="outlined" />
                <Chip label={`Visibili: ${groupEmployeesSummary.visible_employees}`} color="primary" variant="outlined" />
                <Chip label={`Cessazioni programmate: ${groupEmployeesSummary.scheduled_cessations}`} color="warning" variant="outlined" />
                <Chip label={`Cessati effettivi: ${groupEmployeesSummary.effective_cessations}`} variant="outlined" />
                <Chip label={`Eliminati: ${groupEmployeesSummary.deleted_employees}`} color="error" variant="outlined" />
              </Box>

              {scheduledCessationEmployees.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Dipendenti ancora visibili con cessazione programmata
                  </Typography>
                  <List dense>
                    {scheduledCessationEmployees.map((employee) => (
                      <ListItem key={`scheduled-${employee.employee_id}`} disableGutters>
                        <ListItemText
                          primary={`${employee.cognome} ${employee.nome}`}
                          secondary={`${getEmployeeNoticeLabel(employee.reason)} il ${formatDateLabel(employee.data_cessazione)}${employee.matricola ? ` - ${employee.matricola}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {effectiveCessationEmployees.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Dipendenti ancora operativi ma gia cessati
                  </Typography>
                  <List dense>
                    {effectiveCessationEmployees.map((employee) => (
                      <ListItem key={`effective-${employee.employee_id}`} disableGutters>
                        <ListItemText
                          primary={`${employee.cognome} ${employee.nome}`}
                          secondary={`${getEmployeeNoticeLabel(employee.reason)} il ${formatDateLabel(employee.data_cessazione)}${employee.matricola ? ` - ${employee.matricola}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {excludedGroupEmployees.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Dipendenti esclusi dal conteggio operativo
                  </Typography>
                  <List dense>
                    {excludedGroupEmployees.map((employee) => (
                      <ListItem key={`excluded-${employee.employee_id}`} disableGutters>
                        <ListItemText
                          primary={`${employee.cognome} ${employee.nome}`}
                          secondary={
                            employee.reason === 'deleted'
                              ? `${getEmployeeNoticeLabel(employee.reason)}${employee.matricola ? ` - ${employee.matricola}` : ''}`
                              : `${getEmployeeNoticeLabel(employee.reason)} il ${formatDateLabel(employee.data_cessazione)}${employee.matricola ? ` - ${employee.matricola}` : ''}`
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {scheduledCessationEmployees.length === 0 && effectiveCessationEmployees.length === 0 && excludedGroupEmployees.length === 0 && (
                <Alert severity="success">
                  Nessuna differenza: il conteggio visibile coincide con i dipendenti assegnati operativi alla data corrente.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountDetailsOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog aggiungi admin */}
      <Dialog 
        open={addMemberDialogOpen} 
        onClose={() => {
          setAddMemberDialogOpen(false);
          setAddAdminMode('existing');
          setSelectedAdminId(null);
          setSelectedEmployeeForAdmin(null);
          setNewAdminData({
            username: '',
            email: '',
            password: '',
            codice_fiscale: '',
            nome: '',
            cognome: '',
            role: 'admin',
            is_super_admin: false,
          });
          setEmployeeAdminData({
            username: '',
            password: '',
            is_super_admin: false,
          });
          setSelectedEmployeeForAdmin(null);
          setEmployeeSearchFilter({
            nome: '',
            cognome: '',
            matricola: '',
            codice_fiscale: '',
            dipartimento: '',
            stato: '',
            ente: '',
            regione_sede: '',
            tipo_sede: '',
            ruolo: '',
            page: 1,
            limit: 20,
          });
          isInitialMountForAdmin.current = true;
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Aggiungi Admin al Gruppo</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={addAdminMode === 'existing' ? 0 : addAdminMode === 'new' ? 1 : 2} 
              onChange={async (_, v) => {
                const modes: ('existing' | 'new' | 'fromEmployee')[] = ['existing', 'new', 'fromEmployee'];
                setAddAdminMode(modes[v]);
                // Quando si seleziona il tab "Da Dipendente", carica i dati
                if (modes[v] === 'fromEmployee') {
                  await loadFilterData();
                  // Imposta filtri SENZA filtri di default per vedere tutti i dipendenti
                  setEmployeeSearchFilter({
                    nome: '',
                    cognome: '',
                    matricola: '',
                    codice_fiscale: '',
                    dipartimento: '',
                    stato: '', // Nessun filtro di default - mostra tutti gli stati
                    ente: '',
                    regione_sede: '',
                    tipo_sede: '',
                    ruolo: '',
                    page: 1,
                    limit: 100, // Limite ragionevole per il caricamento iniziale
                  });
                  // Reset flag e carica immediatamente
                  isInitialMountForAdmin.current = false;
                  // Carica i dipendenti immediatamente
                  loadFilteredEmployeesForAdmin();
                } else {
                  // Reset flag quando si cambia tab
                  isInitialMountForAdmin.current = true;
                }
              }}
            >
              <Tab label="Admin Esistente" />
              <Tab label="Crea Nuovo Admin" />
              <Tab label="Da Dipendente" />
            </Tabs>
          </Box>

          {addAdminMode === 'existing' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                select
                fullWidth
                label="Seleziona Admin"
                value={selectedAdminId || ''}
                onChange={(e) => setSelectedAdminId(Number(e.target.value))}
                SelectProps={{ native: true }}
              >
                <option value="">Seleziona...</option>
                {allAdmins
                  .filter((admin) => !groupMembers.some((m) => m.id === admin.id))
                  .map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.cognome} {admin.nome} ({admin.email})
                    </option>
                  ))}
              </TextField>
            </Box>
          )}

          {addAdminMode === 'new' && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username *"
                    value={newAdminData.username}
                    onChange={(e) => setNewAdminData({ ...newAdminData, username: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={newAdminData.email}
                    onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password *"
                    type="password"
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Codice Fiscale *"
                    value={newAdminData.codice_fiscale}
                    onChange={(e) => setNewAdminData({ ...newAdminData, codice_fiscale: e.target.value.toUpperCase() })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome *"
                    value={newAdminData.nome}
                    onChange={(e) => setNewAdminData({ ...newAdminData, nome: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cognome *"
                    value={newAdminData.cognome}
                    onChange={(e) => setNewAdminData({ ...newAdminData, cognome: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newAdminData.is_super_admin}
                        onChange={(e) => setNewAdminData({ ...newAdminData, is_super_admin: e.target.checked })}
                      />
                    }
                    label="Super Admin"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Questo admin può non essere un dipendente. Verrà creato solo come utente con privilegi amministrativi.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          )}

          {addAdminMode === 'fromEmployee' && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Cerca e Seleziona Dipendente
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setEmployeeSearchFilter({
                      nome: '',
                      cognome: '',
                      matricola: '',
                      codice_fiscale: '',
                      dipartimento: '',
                      stato: '',
                      ente: '',
                      regione_sede: '',
                      tipo_sede: '',
                      ruolo: '',
                      page: 1,
                      limit: 100,
                    });
                    isInitialMountForAdmin.current = false;
                    setTimeout(() => loadFilteredEmployeesForAdmin(), 100);
                  }}
                >
                  Rimuovi Filtri
                </Button>
              </Box>
              
              {/* Indicatore filtri attivi */}
              {(employeeSearchFilter.nome || employeeSearchFilter.cognome || employeeSearchFilter.codice_fiscale || 
                employeeSearchFilter.matricola || employeeSearchFilter.dipartimento || employeeSearchFilter.stato || 
                employeeSearchFilter.ente || employeeSearchFilter.regione_sede || employeeSearchFilter.tipo_sede || 
                employeeSearchFilter.ruolo) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    Filtri attivi: 
                    {employeeSearchFilter.nome && ` Nome: "${employeeSearchFilter.nome}"`}
                    {employeeSearchFilter.cognome && ` Cognome: "${employeeSearchFilter.cognome}"`}
                    {employeeSearchFilter.codice_fiscale && ` CF: "${employeeSearchFilter.codice_fiscale}"`}
                    {employeeSearchFilter.matricola && ` Matricola: "${employeeSearchFilter.matricola}"`}
                    {employeeSearchFilter.dipartimento && ` Sede: "${employeeSearchFilter.dipartimento}"`}
                    {employeeSearchFilter.stato && ` Stato: "${employeeSearchFilter.stato}"`}
                    {employeeSearchFilter.ente && ` Ente: "${employeeSearchFilter.ente}"`}
                    {employeeSearchFilter.regione_sede && ` Regione: "${employeeSearchFilter.regione_sede}"`}
                    {employeeSearchFilter.tipo_sede && ` Tipo Sede: "${employeeSearchFilter.tipo_sede}"`}
                    {employeeSearchFilter.ruolo && ` Ruolo: "${employeeSearchFilter.ruolo}"`}
                  </Typography>
                </Alert>
              )}
              
              {/* Filtri di ricerca */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Filtri di Ricerca
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <TextField
                    label="Cognome"
                    value={employeeSearchFilter.cognome}
                    onChange={(e) => handleEmployeeSearchFilterChange('cognome', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Nome"
                    value={employeeSearchFilter.nome}
                    onChange={(e) => handleEmployeeSearchFilterChange('nome', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="CF"
                    value={employeeSearchFilter.codice_fiscale}
                    onChange={(e) => handleEmployeeSearchFilterChange('codice_fiscale', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Matricola"
                    value={employeeSearchFilter.matricola}
                    onChange={(e) => handleEmployeeSearchFilterChange('matricola', e.target.value)}
                    size="small"
                    sx={{ minWidth: 120 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Sede</InputLabel>
                    <Select
                      value={employeeSearchFilter.dipartimento}
                      onChange={(e) => handleEmployeeSearchFilterChange('dipartimento', e.target.value)}
                      label="Sede"
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Ente</InputLabel>
                    <Select
                      value={employeeSearchFilter.ente}
                      onChange={(e) => handleEmployeeSearchFilterChange('ente', e.target.value)}
                      label="Ente"
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      {enti.map((ente) => (
                        <MenuItem key={ente} value={ente}>{ente}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Stato</InputLabel>
                    <Select
                      value={employeeSearchFilter.stato}
                      onChange={(e) => handleEmployeeSearchFilterChange('stato', e.target.value)}
                      label="Stato"
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      <MenuItem value="attivo">Attivo</MenuItem>
                      <MenuItem value="inattivo">Inattivo</MenuItem>
                      <MenuItem value="in_ferie">In Ferie</MenuItem>
                      <MenuItem value="in_malattia">In Malattia</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Tabella dipendenti */}
              {loadingEmployeesForAdmin ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Seleziona</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Cognome</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Cod. Fiscale</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Matricola</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Sede</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Ente</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEmployeesForAdmin.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Box sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {loadingEmployeesForAdmin 
                                    ? 'Caricamento...' 
                                    : 'Nessun dipendente trovato'}
                                </Typography>
                                {!loadingEmployeesForAdmin && (
                                  <Typography variant="caption" color="text.secondary">
                                    Prova a modificare i filtri di ricerca.
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredEmployeesForAdmin.map((employee) => {
                            const hasUser = !!employee.user_id;
                            return (
                              <TableRow 
                                key={employee.id}
                                hover
                                selected={selectedEmployeeForAdmin === employee.id}
                                onClick={() => {
                                  setSelectedEmployeeForAdmin(employee.id);
                                  if (!hasUser) {
                                    // Se non ha utente, pre-compila username per creare nuovo utente
                                    const username = `${employee.nome.toLowerCase()}.${employee.cognome.toLowerCase()}`.replace(/\s+/g, '');
                                    setEmployeeAdminData({ ...employeeAdminData, username });
                                  } else {
                                    // Se ha già utente, non serve username/password
                                    setEmployeeAdminData({ ...employeeAdminData, username: '', password: '' });
                                  }
                                }}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={selectedEmployeeForAdmin === employee.id}
                                    onChange={() => {
                                      setSelectedEmployeeForAdmin(employee.id);
                                      if (!hasUser) {
                                        // Se non ha utente, pre-compila username per creare nuovo utente
                                        const username = `${employee.nome.toLowerCase()}.${employee.cognome.toLowerCase()}`.replace(/\s+/g, '');
                                        setEmployeeAdminData({ ...employeeAdminData, username });
                                      } else {
                                        // Se ha già utente, non serve username/password
                                        setEmployeeAdminData({ ...employeeAdminData, username: '', password: '' });
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{employee.cognome}</TableCell>
                                <TableCell>{employee.nome}</TableCell>
                                <TableCell>{employee.codice_fiscale}</TableCell>
                                <TableCell>{employee.matricola || '-'}</TableCell>
                                <TableCell>{employee.dipartimento || '-'}</TableCell>
                                <TableCell>
                                  {hasUser ? (
                                    <Chip label="Ha utente" size="small" color="info" />
                                  ) : (
                                    <Chip label="Nuovo utente" size="small" color="success" />
                                  )}
                                </TableCell>
                                <TableCell>{employee.ente || '-'}</TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredEmployeesForAdminTotal}
                    page={employeeSearchFilter.page - 1}
                    onPageChange={handlePageChangeForAdmin}
                    rowsPerPage={employeeSearchFilter.limit}
                    onRowsPerPageChange={handleChangeRowsPerPageForAdmin}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                  />
                </>
              )}

              {/* Form per creare utente admin dal dipendente selezionato */}
              {selectedEmployeeForAdmin && (() => {
                const selectedEmployee = filteredEmployeesForAdmin.find(emp => emp.id === selectedEmployeeForAdmin);
                const hasUser = !!selectedEmployee?.user_id;
                
                return (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {hasUser ? 'Dipendente con Utente Esistente' : 'Crea Nuovo Account Admin'}
                    </Typography>
                    {hasUser ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Questo dipendente ha già un utente associato (ID: {selectedEmployee.user_id}).
                        Verrà aggiunto al gruppo usando l'utente esistente. Non è necessario creare un nuovo account.
                      </Alert>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          label="Username *"
                          value={employeeAdminData.username}
                          onChange={(e) => setEmployeeAdminData({ ...employeeAdminData, username: e.target.value })}
                          sx={{ mb: 2 }}
                          required
                        />
                        <TextField
                          fullWidth
                          label="Password *"
                          type="password"
                          value={employeeAdminData.password}
                          onChange={(e) => setEmployeeAdminData({ ...employeeAdminData, password: e.target.value })}
                          sx={{ mb: 2 }}
                          required
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={employeeAdminData.is_super_admin}
                              onChange={(e) => setEmployeeAdminData({ ...employeeAdminData, is_super_admin: e.target.checked })}
                            />
                          }
                          label="Super Admin"
                        />
                      </>
                    )}
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddMemberDialogOpen(false);
            setAddAdminMode('existing');
            setSelectedAdminId(null);
            setSelectedEmployeeForAdmin(null);
            setNewAdminData({
              username: '',
              email: '',
              password: '',
              codice_fiscale: '',
              nome: '',
              cognome: '',
              role: 'admin',
              is_super_admin: false,
            });
            setEmployeeAdminData({
              username: '',
              password: '',
              is_super_admin: false,
            });
          }}>
            Annulla
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={
              addAdminMode === 'existing' ? !selectedAdminId :
              addAdminMode === 'new' ? !newAdminData.username || !newAdminData.password || !newAdminData.email :
              !selectedEmployeeForAdmin || (() => {
                // Se il dipendente non ha utente, username e password sono obbligatori
                const selectedEmployee = filteredEmployeesForAdmin.find(emp => emp.id === selectedEmployeeForAdmin);
                if (!selectedEmployee?.user_id) {
                  return !employeeAdminData.username || !employeeAdminData.password;
                }
                // Se ha già utente, non serve username/password
                return false;
              })()
            }
          >
            {addAdminMode === 'existing' ? 'Aggiungi' : addAdminMode === 'new' ? 'Crea e Aggiungi' : 'Converti e Aggiungi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog aggiungi dipendente con filtri */}
      <Dialog 
        open={addEmployeeDialogOpen} 
        onClose={() => {
          setAddEmployeeDialogOpen(false);
          setSelectedEmployeeIds([]);
          setEmployeeFilter({
            nome: '',
            cognome: '',
            matricola: '',
            codice_fiscale: '',
            dipartimento: '',
            stato: '',
            ente: '',
            regione_sede: '',
            tipo_sede: '',
            ruolo: '',
            page: 1,
            limit: 20,
          });
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Aggiungi Dipendenti al Gruppo: {selectedGroup?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Filtri */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Filtri di Ricerca
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <TextField
                  label="Cognome"
                  value={employeeFilter.cognome}
                  onChange={(e) => handleEmployeeFilterChange('cognome', e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Nome"
                  value={employeeFilter.nome}
                  onChange={(e) => handleEmployeeFilterChange('nome', e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="CF"
                  value={employeeFilter.codice_fiscale}
                  onChange={(e) => handleEmployeeFilterChange('codice_fiscale', e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Matricola"
                  value={employeeFilter.matricola}
                  onChange={(e) => handleEmployeeFilterChange('matricola', e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Sede</InputLabel>
                  <Select
                    value={employeeFilter.dipartimento}
                    onChange={(e) => handleEmployeeFilterChange('dipartimento', e.target.value)}
                    label="Sede"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Ente</InputLabel>
                  <Select
                    value={employeeFilter.ente}
                    onChange={(e) => handleEmployeeFilterChange('ente', e.target.value)}
                    label="Ente"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {enti.map((ente) => (
                      <MenuItem key={ente} value={ente}>{ente}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={employeeFilter.stato}
                    onChange={(e) => handleEmployeeFilterChange('stato', e.target.value)}
                    label="Stato"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    <MenuItem value="attivo">Attivo</MenuItem>
                    <MenuItem value="inattivo">Inattivo</MenuItem>
                    <MenuItem value="in_ferie">In Ferie</MenuItem>
                    <MenuItem value="in_malattia">In Malattia</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Regione</InputLabel>
                  <Select
                    value={employeeFilter.regione_sede}
                    onChange={(e) => handleEmployeeFilterChange('regione_sede', e.target.value)}
                    label="Regione"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {regioni.map((regione) => (
                      <MenuItem key={regione} value={regione}>{regione}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Tipo Sede</InputLabel>
                  <Select
                    value={employeeFilter.tipo_sede}
                    onChange={(e) => handleEmployeeFilterChange('tipo_sede', e.target.value)}
                    label="Tipo Sede"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {tipiSede.map((tipoSede) => (
                      <MenuItem key={tipoSede} value={tipoSede}>{tipoSede}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Tipo Ruolo</InputLabel>
                  <Select
                    value={employeeFilter.ruolo}
                    onChange={(e) => handleEmployeeFilterChange('ruolo', e.target.value)}
                    label="Tipo Ruolo"
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    {ruoli.map((ruolo) => (
                      <MenuItem key={ruolo} value={ruolo}>{ruolo}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Info selezione */}
            {selectedEmployeeIds.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedEmployeeIds.length} dipendente/i selezionato/i
              </Alert>
            )}

            {/* Tabella dipendenti */}
            {loadingEmployees ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selectedEmployeeIds.length > 0 &&
                              selectedEmployeeIds.length < filteredEmployees.length
                            }
                            checked={
                              filteredEmployees.length > 0 &&
                              filteredEmployees.every(e => selectedEmployeeIds.includes(e.id))
                            }
                            onChange={handleSelectAllEmployees}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Cognome</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Cod. Fiscale</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Matricola</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Sede</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ente</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredEmployees.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              Nessun dipendente trovato
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredEmployees.map((employee) => (
                          <TableRow 
                            key={employee.id}
                            hover
                            selected={selectedEmployeeIds.includes(employee.id)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedEmployeeIds.includes(employee.id)}
                                onChange={() => handleSelectEmployee(employee.id)}
                              />
                            </TableCell>
                            <TableCell>{employee.cognome}</TableCell>
                            <TableCell>{employee.nome}</TableCell>
                            <TableCell>{employee.codice_fiscale}</TableCell>
                            <TableCell>{employee.matricola || '-'}</TableCell>
                            <TableCell>{employee.dipartimento || '-'}</TableCell>
                            <TableCell>{employee.ente || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={employee.stato || 'attivo'}
                                size="small"
                                color={
                                  employee.stato === 'attivo' ? 'success' :
                                  employee.stato === 'inattivo' ? 'error' :
                                  employee.stato === 'in_ferie' ? 'warning' : 'default'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredEmployeesTotal}
                  page={employeeFilter.page - 1}
                  onPageChange={handlePageChange}
                  rowsPerPage={employeeFilter.limit}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Righe per pagina:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddEmployeeDialogOpen(false);
            setSelectedEmployeeIds([]);
            setEmployeeFilter({
              nome: '',
              cognome: '',
              matricola: '',
              codice_fiscale: '',
              dipartimento: '',
              stato: '',
              ente: '',
              regione_sede: '',
              tipo_sede: '',
              ruolo: '',
              page: 1,
              limit: 20,
            });
          }}>
            Annulla
          </Button>
          <Button
            onClick={handleAddEmployee}
            variant="contained"
            disabled={selectedEmployeeIds.length === 0}
          >
            Aggiungi {selectedEmployeeIds.length > 0 ? `(${selectedEmployeeIds.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog di conferma prima dell'invio credenziali */}
      <Dialog 
        open={confirmSendDialog} 
        onClose={() => !sendingCreds && setConfirmSendDialog(false)}
        disableEnforceFocus
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Conferma Invio Credenziali</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Stai per inviare credenziali temporanee a {pendingRecipients.length} utente/i
            </Typography>
            <Typography variant="body2">
              Verrà generata una nuova password temporanea per ogni utente e inviata via email. 
              Gli utenti dovranno cambiare la password al primo accesso.
            </Typography>
          </Alert>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
            Destinatari ({pendingRecipients.length}):
          </Typography>
          
          <TableContainer sx={{ mt: 1, maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingRecipients.map((recipient, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Chip 
                        label={recipient.type === 'admin' ? 'Admin' : 'Dipendente'} 
                        size="small" 
                        color={recipient.type === 'admin' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>{recipient.name}</TableCell>
                    <TableCell>
                      {recipient.email ? (
                        recipient.email
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Email non disponibile
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendDialog(false)}>
            Annulla
          </Button>
          <Button 
            onClick={handleConfirmSendCredentials} 
            variant="contained" 
            color="primary"
            disabled={sendingCreds}
            startIcon={sendingCreds ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {sendingCreds ? 'Invio in corso...' : 'Conferma e Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per mostrare le credenziali generate */}
      <Dialog 
        open={credentialsDialog} 
        onClose={handleCloseCredentialsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Credenziali Inviate con Successo</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Le credenziali sono state inviate via email. Salva queste informazioni per riferimento.
          </Alert>
          
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Credenziali Generate:
          </Typography>
          
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Password Temporanea</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generatedCredentials.map((cred, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        value={cred.username}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </TableCell>
                    <TableCell>{cred.email}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={cred.password}
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCredentialsDialog} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminGroupsManagement;
